import { useEffect, useRef, useState } from 'react';
import { History } from 'lucide-react';
import { useT } from '../i18n';
import type { ProjectEvent } from '../types';

// "Historique (n)" in a card's header: the changes made to that card's fields
// after their stage was passed (`field_changed` project events, logged by the
// server), newest first, each with who, when, and the value before and after.

/** The field changes among the project's events, for the given field keys. */
export function fieldChanges(events: ProjectEvent[], keys: string[]): ProjectEvent[] {
  return events.filter(e => e.event_type === 'field_changed' && keys.includes(String(e.details?.field)));
}

export function FieldHistory({ events, keys }: { events: ProjectEvent[]; keys: string[] }) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const list = fieldChanges(events, keys).sort((a, b) => b.created_at.localeCompare(a.created_at));
  if (list.length === 0) return null;
  const when = (iso: string) => new Date(iso).toLocaleString(lang === 'fr' ? 'fr-CH' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const value = (v: unknown) => (String(v ?? '').trim() ? String(v) : t('projectDetail.history.empty'));

  return (
    <div className="fh" ref={ref}>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(v => !v)} aria-expanded={open}>
        <History size={12} /> {t('projectDetail.history.button', { n: list.length })}
      </button>
      {open && (
        <div className="fh-pop" role="dialog" aria-label={t('projectDetail.history.title')}>
          <div className="fh-title">{t('projectDetail.history.title')}</div>
          <ul className="fh-list">
            {list.map(e => (
              <li key={e.id}>
                <div className="fh-meta">
                  <strong>{t(`projectDetail.history.field.${e.details?.field}`)}</strong>
                  <span>{when(e.created_at)} · {e.actor_name}</span>
                </div>
                <div className="fh-change">
                  <span className="fh-old" title={String(e.details?.old ?? '')}>{value(e.details?.old)}</span>
                  <span className="fh-arrow">→</span>
                  <span className="fh-new" title={String(e.details?.new ?? '')}>{value(e.details?.new)}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
